// Team Discipline — types + shaping (Phase 3).
//
// A stat-bar list of a team's disciplinary rates: yellow/red cards, fouls,
// offsides and penalties conceded, over a window (Last 5 / Last 10 / Season) and
// a rate (Per Game / Total). Each bar is scaled to the LEAGUE-WIDE max for that
// stat + window + rate, so bars are comparable across teams. Pure + framework
// free; the component owns the bar colours + icons and animates width via CSS.

/** One team's disciplinary figures for a single window (per-game + total). */
export interface TeamDisciplineWindow {
  yc: number;
  rc: number;
  fouls: number;
  off: number;
  pens: number;
  ycT: number;
  rcT: number;
  foulsT: number;
  offT: number;
  pensT: number;
}

/** A team's figures across all three windows. */
export type TeamDisciplineTeam = Record<"5" | "10" | "Season", TeamDisciplineWindow>;

/** Team Discipline data keyed by full team name. */
export type TeamDisciplineData = Record<string, TeamDisciplineTeam>;

/** Timeline control values understood by the widget. */
export type DisciplineWindow = "5" | "10" | "Season" | string;

/** One stat bar: its value, the league max (bar scale), and the formatted text. */
export interface StatBarRow {
  key: "yc" | "rc" | "fouls" | "off" | "pens";
  val: number;
  max: number;
  text: string;
}

/** Shaped widget: a section header + the five stat bars. */
export interface TeamDisciplineShape {
  header: string;
  rows: StatBarRow[];
}

/** Bar width as a percentage string, clamped to 100 — matches the legacy
 *  `Math.min(100, (val / (max || 1)) * 100).toFixed(1)`. */
export function statBarPct(val: number, max: number): string {
  return Math.min(100, (val / (max || 1)) * 100).toFixed(1);
}

// The five stats in display order, with which fields to read per rate and the
// per-game decimal precision (Total is always integer, printed verbatim).
const STATS: {
  key: StatBarRow["key"];
  pg: keyof TeamDisciplineWindow;
  total: keyof TeamDisciplineWindow;
  dec: number;
}[] = [
  { key: "yc", pg: "yc", total: "ycT", dec: 1 },
  { key: "rc", pg: "rc", total: "rcT", dec: 2 },
  { key: "fouls", pg: "fouls", total: "foulsT", dec: 1 },
  { key: "off", pg: "off", total: "offT", dec: 1 },
  { key: "pens", pg: "pens", total: "pensT", dec: 2 },
];

/**
 * Shape a team's discipline bars for a window + rate. `max` per stat is the
 * league-wide maximum (across every team's same-window figures), so bars are
 * comparable. Returns null when the team/window is missing (empty state).
 */
export function shapeTeamDiscipline(
  data: TeamDisciplineData,
  team: string,
  timeline: DisciplineWindow,
  isTotal: boolean
): TeamDisciplineShape | null {
  const teamData = data[team];
  const tl = timeline as "5" | "10" | "Season";
  const win = teamData ? teamData[tl] : undefined;
  if (!win) return null;

  const windows = Object.values(data)
    .map((t) => t[tl])
    .filter(Boolean);
  const maxOf = (field: keyof TeamDisciplineWindow): number =>
    Math.max(...windows.map((w) => w[field]));

  const period = tl === "Season" ? "Season" : `Last ${tl}`;
  const rows: StatBarRow[] = STATS.map(({ key, pg, total, dec }) => {
    const field = isTotal ? total : pg;
    const val = win[field];
    return {
      key,
      val,
      max: maxOf(field),
      text: isTotal ? String(val) : val.toFixed(dec),
    };
  });

  return { header: `${team} – ${period}`, rows };
}
